import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import { AppRoutes } from "AppRoutes";
import DocumentName from "components/DocumentName";
import {
  AiChatWithMessagesView,
  AiContinueChatDocument,
  AiContinueChatMutation,
  AiRunRevisedChatDocument,
  CreateTestCaseFromChatDocument,
} from "generated/graphql";
import { runGqlMutation } from "gqlClient";
import { useSnackbar } from "notistack";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLoaderData } from "react-router-dom";
import { P } from "theme/typography";
import AiChatMessageComponent from "./ChatMessage";
import LoadingPattern from "components/LoadingPattern";
import LoadingSpinner from "components/LoadingSpinner";
import { Send } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import theme from "theme";
import PSelect from "components/PSelect";
import { Annotation } from "components/RainbowText";
import { collectFirst } from "helpers/coll";
import { extractGQLError } from "shared/PatternGraphQLError";
import PCheckbox from "components/PCheckbox";
import { formatRelativeTime } from "Backend";
import { AiChatMessageViewWithIndex } from "./aiChatLoader";

const PROMPT_EVALUATE_PROMPT =
  "Thanks for your help, now that you've completed the task, please evaluate the clarity of the instructions you were provided. How would you suggest clarifying the instructions for future tasks?";

export const AiChat: FC = () => {
  const [continuedFullDialog, setContinuedFullDialog] = useState<
    AiContinueChatMutation["lens"]["aiChat"]["continueConversation"] | undefined
  >(undefined);
  const chat = useLoaderData() as AiChatWithMessagesView;
  const [continueChatLoading, setContinueChatLoading] = useState<boolean>(false);
  const [pendingMessage, setPendingMessage] = useState<string | undefined>(undefined);
  const chatbox = useRef<HTMLDivElement>(null);
  const [chatInputText, setChatInputText] = useState<string>("");
  const [logFullRequest, setLogFullRequest] = useState(false);
  const [editedMessages, setEditedMessages] = useState<AiChatMessageViewWithIndex[]>([]);
  const [editIsPending, setEditIsPending] = useState<boolean>(false);
  const [isEditPreview, setIsEditPreview] = useState<boolean>(false);
  const [model, setModel] = useState(chat.chat.model);
  const [forcedModel, setForcedModel] = useState<string | undefined>(undefined);
  const [totalUsage, setTotalUsage] = useState(chat.chat.completionUsage + chat.chat.promptUsage);
  const { enqueueSnackbar } = useSnackbar();
  const [matches, setMatches] = useState<Annotation[]>([]);
  const sendChatMessage = (chatId: string) => {
    setContinueChatLoading(true);
    setPendingMessage(chatInputText);
    setChatInputText("");
    runGqlMutation(AiContinueChatDocument, {
      lensId: chat.chat.lensId,
      chatId: chatId,
      message: chatInputText,
      forceModel: forcedModel,
      logFullRequest,
    })
      .then((res) => {
        setPendingMessage(undefined);
        setContinueChatLoading(false);
        setContinuedFullDialog(res.lens.aiChat.continueConversation);
      })
      .catch((err) => {
        setPendingMessage(undefined);
        setContinuedFullDialog(undefined);
        setContinueChatLoading(false);
        enqueueSnackbar(err.toString());
      });
  };

  // This could be avoided if we could just re-create this entire component
  // when its chatId changes, but I don't see a way to do that in the react
  // router docs; invalidating the route is not the same thing
  useEffect(() => {
    setModel(chat.chat.model);
    setTotalUsage(chat.chat.promptUsage + chat.chat.completionUsage);
  }, [chat.chat]);

  const messagesToRender: AiChatMessageViewWithIndex[] = (
    continuedFullDialog ? continuedFullDialog.fullDialog : chat.messages
  ).map((msg, index) => ({ ...msg, index }));
  const highlightTargetMessage = messagesToRender.find(
    (m) => m.format.__typename === "Blocks" || m.format.__typename === "PageImage"
  );
  const highlightTargetText: string =
    highlightTargetMessage?.format.__typename === "Blocks"
      ? highlightTargetMessage?.content ?? ""
      : collectFirst(highlightTargetMessage?.contentParts ?? [], (part) => part.textContent) ?? "";

  // TODO: Just search all the references instead of on click just one
  const updateMatches = (reference: string) => {
    const text = highlightTargetText;
    const regexp = new RegExp(reference, "g");
    const matches = [...text.matchAll(regexp)];
    const ranges = matches.map((match) => ({
      start: match.index,
      end: match.index + match[0].length,
      tag: "",
      highlight: true,
      color: "yellow",
    }));
    setMatches(ranges);
  };

  const firstOccurenceOfAssistantMessage = useMemo(() => {
    const indexOfAsstMsg = messagesToRender.find((m) => m.role === "assistant")?.index ?? -1;
    return indexOfAsstMsg === -1 ? messagesToRender.length : indexOfAsstMsg;
  }, [messagesToRender]);
  const handleRunRevisedChat = () => {
    setEditIsPending(true);
    runGqlMutation(AiRunRevisedChatDocument, {
      lensId: chat.chat.lensId,
      chatId: chat.chat.id,
      revisedMessages: editedMessages.map((m) => ({
        messageIndex: m.index,
        content: m.content ?? "",
      })),
      forceModel: forcedModel,
      logFullRequest,
    })
      .then((res) => {
        if (res.lens.aiChat.runRevisedChat.fullDialog) {
          setContinuedFullDialog(res.lens.aiChat.runRevisedChat);
          setModel(res.lens.aiChat.runRevisedChat.model);
          setTotalUsage(res.lens.aiChat.runRevisedChat.usage.totalTokens);
          setIsEditPreview(true);
          setEditedMessages([]);
          setMatches([]);
        }
        setEditIsPending(false);
      })
      .catch(() => {
        setEditIsPending(false);
        enqueueSnackbar("Failed to update messages");
      });
  };

  // we need to reset the continued or the chat will no change
  useEffect(() => {
    setContinuedFullDialog(undefined);
    setEditedMessages([]);
    setIsEditPreview(false);
  }, [chat.messages]);

  // scroll to bottom on every new chat or chat change
  useEffect(() => {
    if (chatbox.current) {
      chatbox.current.scrollTop = chatbox.current.scrollHeight;
    }
  }, [messagesToRender, pendingMessage]);

  const models = ["gpt-35-turbo", "gpt-4-turbo", "gpt-4o"];

  const handleCreateTestCase = () => {
    runGqlMutation(CreateTestCaseFromChatDocument, {
      lensId: chat.chat.lensId,
      aiChatId: chat.chat.id,
      description: JSON.stringify({ text: "", labels: [] }),
      correctedValues: [],
    })
      .then((_res) => {
        enqueueSnackbar("Test case created", { variant: "info" });
      })
      .catch((err) => {
        const gql = extractGQLError(err);
        enqueueSnackbar(`Failed to create test case: ${gql?.message ?? "unknown"}`, { variant: "error" });
      });
  };

  return (
    <div className="flex flex-col overflow-auto">
      <div className="flex flex-col m-1">
        <div className="flex flex-row gap-1">
          {chat.chat.failureReason && (
            <p className="text-xs">
              <b>Failure Reason</b>: {chat.chat.failureReason}
            </p>
          )}
          <p className="text-xs">
            <b>Date</b>: {formatRelativeTime(chat.chat.createdAt)}
          </p>
          <p className="text-xs">
            <b>Model</b>: {model}
          </p>
          <p className="text-xs">
            <b>Prompt Usage</b>: {chat.chat.promptUsage}
          </p>
          <p className="text-xs">
            <b>Completion Usage</b>: {chat.chat.completionUsage}
          </p>
          <p className="text-xs">
            <b>Total Usage</b>: {totalUsage}
          </p>
        </div>
        <div className="flex flex-row mt-2 items-center gap-2">
          <Link
            to={AppRoutes.caseDocumentsBuilder({
              lensId: chat.chat.lensId,
              caseId: chat.chat.caseId,
              documentId: chat.chat.documentIntakeId || undefined,
              page: chat.chat.startIntakePage || undefined,
            })}>
            <DocumentName className="max-w-96" name={chat.chat.documentName || "<no name>"} />
          </Link>
          <p className="text-xs">Start Page: {chat.chat.startIntakePage}</p>
          <Button size="small" variant="contained" onClick={handleCreateTestCase}>
            + Create Test Case
          </Button>
        </div>
      </div>
      {isEditPreview ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1,
            backgroundColor: theme.palette.primary.light,
          }}>
          <P variant="lgBold" align="center">
            You are viewing an updated chat preview.
          </P>
          <Box
            sx={{
              display: "flex",
              gap: 1,
            }}>
            <Button
              onClick={() => {
                setContinuedFullDialog(undefined);
                setIsEditPreview(false);
              }}>
              Clear
            </Button>
          </Box>
        </Box>
      ) : null}
      {editedMessages.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1,
            backgroundColor: theme.palette.primary.light,
          }}>
          <P variant="lgBold" align="center">
            {editedMessages.length} messages updated
          </P>
          <Box
            sx={{
              display: "flex",
              gap: 1,
            }}>
            <Button
              disabled={editIsPending}
              onClick={() => {
                setEditedMessages([]);
              }}>
              Cancel
            </Button>
            <LoadingButton variant="contained" loading={editIsPending} onClick={() => handleRunRevisedChat()}>
              Send
            </LoadingButton>
          </Box>
        </Box>
      ) : null}
      <Divider />
      <Box
        ref={chatbox}
        sx={{
          flex: 1,
          maxWidth: "100%",
          overflow: "auto",
          scrollBehavior: "smooth",
          px: 1,
          pt: 1,
        }}>
        {messagesToRender.map((message) => (
          <AiChatMessageComponent
            updateMessage={(editedMessage) => {
              setEditedMessages((prev) => {
                const newEditedMessages = [...prev];
                const messageIndex = newEditedMessages.findIndex((m) => m.index === editedMessage.messageIndex);
                const editedFullMessage = {
                  ...message,
                  content: editedMessage.content,
                };
                if (messageIndex === -1) {
                  newEditedMessages.push(editedFullMessage);
                } else {
                  newEditedMessages[messageIndex] = editedFullMessage;
                }
                return newEditedMessages;
              });
            }}
            key={`${message.index}_${message.content}`}
            message={editedMessages.find((m) => m.index === message.index) ?? message}
            matchesHandle={[matches, setMatches]}
            updateMatches={updateMatches}
            editable={message.index < firstOccurenceOfAssistantMessage && !isEditPreview && !editIsPending}
          />
        ))}
        {pendingMessage && (
          <AiChatMessageComponent
            updateMatches={updateMatches}
            matchesHandle={[matches, setMatches]}
            message={{
              role: "user",
              format: { __typename: "ManualMessage" },
              content: pendingMessage,
              index: messagesToRender.length,
            }}
          />
        )}
        {pendingMessage && (
          <Box sx={{ p: 2 }}>
            <LoadingPattern size={20} centered />
          </Box>
        )}
      </Box>
      <div className="flex p-2 mx-2 bg-white rounded-md shadow">
        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, mr: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Button
              size="small"
              disabled={continueChatLoading || editIsPending}
              sx={{ width: "200px" }}
              onClick={() => setChatInputText(PROMPT_EVALUATE_PROMPT)}>
              Evaluate Prompt
            </Button>
          </Box>
          <OutlinedInput
            fullWidth
            multiline
            rows={3}
            value={chatInputText}
            onChange={(text) => setChatInputText(text.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  disabled={continueChatLoading || editIsPending}
                  onClick={() => sendChatMessage(chat.chat.id)}>
                  {continueChatLoading ? <LoadingSpinner size={20} /> : <Send />}
                </IconButton>
              </InputAdornment>
            }
            disabled={continueChatLoading}
            placeholder={continueChatLoading ? "Conquering the world..." : "Type your message"}
          />
        </Box>

        <div className="flex flex-col">
          <PCheckbox
            label="Log Full Request"
            checked={logFullRequest}
            onChange={(evt) => setLogFullRequest(evt.target.checked)}
          />
          <FormControl>
            <InputLabel>Force Model</InputLabel>
            <PSelect
              value={forcedModel ?? ""}
              onChange={(evt) => setForcedModel((evt.target.value as string) || undefined)}>
              <MenuItem value="">None</MenuItem>
              {models.map((model) => (
                <MenuItem key={model} value={model}>
                  {model}
                </MenuItem>
              ))}
            </PSelect>
          </FormControl>
        </div>
      </div>
    </div>
  );
};
